import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import { ScraperJob, AzureIndex } from "./types";

interface PublishToIndexModalProps {
  job: ScraperJob;
  indexes: AzureIndex[];
  onClose: () => void;
  onPublish: (indexId: number) => Promise<void>;
}

export default function PublishToIndexModal({
  job,
  indexes,
  onClose,
  onPublish
}: PublishToIndexModalProps) {
  const [selectedIndexId, setSelectedIndexId] = useState<number | null>(
    indexes.length > 0 ? indexes[0].id : null
  );
  const [publishing, setPublishing] = useState(false);

  const handlePublish = async () => {
    if (!selectedIndexId) return;

    setPublishing(true);
    try {
      await onPublish(selectedIndexId);
      onClose();
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Modal show={true}>
      <ModalHeader title="Publish to Index" onClose={onClose} />
      <ModalBody>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Publish content from <strong>{job.name}</strong> to an Azure Search
            index. This will copy {job.pages_scraped} page(s) to the selected
            index.
          </p>

          {indexes.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-700">
                No indexes available. Please create an index first in the
                Knowledge Indexes page.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Index
              </label>
              <select
                value={selectedIndexId || ""}
                onChange={(e) => setSelectedIndexId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {indexes.map((index) => (
                  <option key={index.id} value={index.id}>
                    {index.name} ({index.type})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-blue-700">
              After publishing, your chatbot's knowledge will update within a few minutes.
              You'll be taken to <strong>Test Chat</strong> to try it right away.
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <SecondaryBlueButton label="Cancel" onClick={onClose} />
        <PrimaryBlueButton
          label={publishing ? "Publishing..." : "Publish"}
          onClick={handlePublish}
          disabled={!selectedIndexId || publishing || indexes.length === 0}
        />
      </ModalFooter>
    </Modal>
  );
}
